import { IonButton, IonIcon, IonText, useIonToast } from '@ionic/react';
import {
  closeOutline,
  folderOpenOutline,
  refreshOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { on, once, removeListener, run } from '../../../helper/electron';
import log from '../../../utils/logger';
import { FolderManager } from '../FolderManager';
import './MusicSourcesManager.scss';
import ResetAppData from './ResetAppData';
import { MusicSourcesManagerProps } from './types';

const LOCAL_TRACK_COUNT_IPC_TIMEOUT_MS = 30_000;

export default function MusicSourcesManager({
  className = '',
}: MusicSourcesManagerProps) {
  const [present] = useIonToast();
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [localTrackCount, setLocalTrackCount] = useState<number | null>(null);
  const [isLoadingLocalCount, setIsLoadingLocalCount] = useState(false);
  const [isClearingLocalTracks, setIsClearingLocalTracks] = useState(false);
  const [deletingTotals, setDeletingTotals] = useState<{
    tracksDeleted: number;
    tracksUpdated: number;
    perPath?: Array<{
      path: string;
      toDelete: number;
      deleted: number;
      updated: number;
    }>;
  } | null>(null);

  const localCountRequestRef = useRef(0);
  const localCountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleBulkDeleteProgress = (resp: any) => {
      try {
        if (resp && resp.success) {
          setDeletingTotals({
            tracksDeleted: Number(resp.tracksDeleted || 0),
            tracksUpdated: Number(resp.tracksUpdated || 0),
            perPath: resp.perPath,
          });
          // Clear display after a short delay
          setTimeout(() => setDeletingTotals(null), 2500);
        }
      } catch {}
    };
    const handleSelectionChanged = (e: any) =>
      setSelectedCount(Number(e?.detail?.selected || 0));
    window.addEventListener(
      'folder-manager/selection-changed',
      handleSelectionChanged,
    );
    return () => {
      removeListener('my-music/delete-folders-bulk');
      window.removeEventListener(
        'folder-manager/selection-changed',
        handleSelectionChanged,
      );
    };
  }, []); // Empty dependency array - only run on mount

  const fetchLocalTrackCount = useCallback(() => {
    if (localCountTimeoutRef.current) {
      clearTimeout(localCountTimeoutRef.current);
      localCountTimeoutRef.current = null;
    }

    const requestId = ++localCountRequestRef.current;
    setIsLoadingLocalCount(true);

    localCountTimeoutRef.current = setTimeout(() => {
      if (localCountRequestRef.current !== requestId) {
        return;
      }
      localCountTimeoutRef.current = null;
      setIsLoadingLocalCount(false);
      log.warn(
        '[MusicSourcesManager] Timed out waiting for local track count IPC',
      );
      present({
        message:
          'Timed out loading Add Local Files track count. If your library is large or still importing, wait a moment and refresh the page.',
        duration: 5000,
        color: 'warning',
      });
    }, LOCAL_TRACK_COUNT_IPC_TIMEOUT_MS);

    once('my-music/get-local-track-count', (payload) => {
      if (localCountRequestRef.current !== requestId) {
        return;
      }
      if (localCountTimeoutRef.current) {
        clearTimeout(localCountTimeoutRef.current);
        localCountTimeoutRef.current = null;
      }
      setIsLoadingLocalCount(false);
      if (!payload || typeof payload !== 'object') {
        console.warn(
          '[MusicSourcesManager] Unexpected payload for local track count:',
          payload,
        );
        present({
          message: 'Unable to load local track count.',
          duration: 3000,
          color: 'danger',
        });
        return;
      }

      if (payload.success) {
        setLocalTrackCount(Number(payload.count || 0));
        return;
      }

      console.error(
        '[MusicSourcesManager] Failed to load local track count:',
        payload,
      );
      present({
        message: payload.error || 'Failed to load local track count',
        duration: 3000,
        color: 'danger',
      });
    });
    run('my-music/get-local-track-count');
  }, [present]);

  useEffect(() => {
    fetchLocalTrackCount();
    return () => {
      localCountRequestRef.current += 1;
      if (localCountTimeoutRef.current) {
        clearTimeout(localCountTimeoutRef.current);
        localCountTimeoutRef.current = null;
      }
    };
  }, [fetchLocalTrackCount]);

  const handleRefresh = () => {
    present({
      message: 'Refreshing music sources...',
      duration: 1500,
      color: 'primary',
    });
  };

  const handleClearLocalTracks = useCallback(() => {
    if (isClearingLocalTracks) {
      return;
    }
    setIsClearingLocalTracks(true);
    once('my-music/clear-local-tracks', (payload) => {
      setIsClearingLocalTracks(false);
      if (payload?.success) {
        const deleted = payload.deleted ?? 0;
        const updated = payload.updated ?? 0;
        present({
          message: `Removed ${deleted.toLocaleString()} tracks${
            updated ? ` · updated ${updated.toLocaleString()}` : ''
          }`,
          duration: 3500,
          color: 'success',
        });
        fetchLocalTrackCount();
        run('track/get-statistics');
        window.dispatchEvent(
          new CustomEvent('library-refresh', {
            detail: { reason: 'clear-local-tracks' },
          }),
        );
      } else {
        present({
          message: payload?.error || 'Failed to clear local tracks',
          duration: 3500,
          color: 'danger',
        });
      }
    });
    run('my-music/clear-local-tracks');
  }, [fetchLocalTrackCount, isClearingLocalTracks, present]);

  const handleFolderImportComplete = (importedCount?: number) => {
    console.log(
      '[MusicSourcesManager] Folder import completed:',
      importedCount,
    );
    if (importedCount && importedCount > 0) {
      present({
        message: `Successfully imported ${importedCount} tracks from folders`,
        duration: 3000,
        color: 'success',
      });
    } else {
      present({
        message: 'Folder operation completed',
        duration: 2000,
        color: 'primary',
      });
    }
  };

  return (
    <div className={`music-sources-manager ${className}`}>
      {/* DJ Software Section */}
      {/* Removed DJ software section */}

      {/* Folder Management Section */}
      <div className="folder-management-section">
        <div
          className="section-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ margin: 0 }}>Music Folders</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IonButton
              fill="solid"
              color="primary"
              size="small"
              className="sources-btn"
              onClick={() => {
                // Prefer dispatching to FolderManager to keep logic centralized
                window.dispatchEvent(
                  new CustomEvent('folder-manager/add-folder'),
                );
              }}
              title="Add folder"
            >
              <IonIcon icon={folderOpenOutline} slot="start" /> Add Folder
            </IonButton>
            <IonButton
              fill="outline"
              size="small"
              className="sources-btn"
              onClick={() => {
                const changed = new Set<string>();
                const folderChanges = new Map<string, any>();
                const normalizePath = (p: string) =>
                  p.replace(/\\/g, '/').toLowerCase();
                const handleBatch = (statuses: any) => {
                  const statusArray = Array.isArray(statuses)
                    ? statuses
                    : [statuses];
                  try {
                    statusArray.forEach((s) => {
                      const hasChanges =
                        s?.needsSync ||
                        (s?.changes && s.changes.total > 0) ||
                        (s?.counts && s.counts.disk !== s.counts.db);
                      if (hasChanges) {
                        const pathValue = s?.folderPath || s?.path;
                        if (pathValue) {
                          const normalizedKey = normalizePath(pathValue);
                          changed.add(pathValue);
                          let changeData = s?.changes;
                          if (!changeData && s?.counts) {
                            const added = Math.max(
                              0,
                              s.counts.disk - s.counts.db,
                            );
                            const deleted = Math.max(
                              0,
                              s.counts.db - s.counts.disk,
                            );
                            changeData = {
                              added,
                              deleted,
                              total: added + deleted,
                            };
                          }
                          if (changeData && changeData.total > 0) {
                            folderChanges.set(normalizedKey, changeData);
                          }
                        }
                      }
                    });
                  } catch {}
                };
                const handleComplete = () => {
                  try {
                    removeListener('my-music/folders-status');
                    removeListener('my-music/folder-status');
                    removeListener('my-music/folders-status-complete');
                  } catch {}
                  if (changed.size === 0) {
                    present({
                      message: 'No changes detected',
                      duration: 2000,
                      color: 'medium',
                    });
                  } else {
                    present({
                      message: `Changes detected in ${changed.size} folder${changed.size === 1 ? '' : 's'}`,
                      duration: 3000,
                      color: 'primary',
                    });
                    const changesObject = Object.fromEntries(folderChanges);
                    window.dispatchEvent(
                      new CustomEvent('folder-manager/refresh-status', {
                        detail: {
                          changedPaths: Array.from(changed),
                          changes: changesObject,
                        },
                      }),
                    );
                  }
                  changed.clear();
                  folderChanges.clear();
                };

                on('my-music/folders-status', handleBatch as any);
                on('my-music/folder-status', handleBatch as any);
                once('my-music/folders-status-complete', handleComplete as any);
                run('my-music/folders-status', { adaptive: true });
              }}
              title="Quickly check each folder for changes"
            >
              <IonIcon icon={refreshOutline} slot="start" /> Check for Changes
            </IonButton>
          </div>
        </div>

        <div
          className="local-files-summary"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            border: '1px solid var(--ion-color-light)',
            borderRadius: 8,
            marginTop: 16,
            marginBottom: 16,
            background: 'var(--ion-color-light-tint)',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong>Tracks added via Add Local Files (Crate Import)</strong>
            <span style={{ color: 'var(--ion-color-medium)' }}>
              {isLoadingLocalCount
                ? 'Loading…'
                : `${(localTrackCount ?? 0).toLocaleString()} tracks`}
            </span>
          </div>
          <IonButton
            color="danger"
            fill="outline"
            size="small"
            disabled={isClearingLocalTracks || (localTrackCount ?? 0) === 0}
            onClick={handleClearLocalTracks}
          >
            {isClearingLocalTracks ? 'Clearing…' : 'Clear Local Files'}
          </IonButton>
        </div>

        {deletingTotals && (
          <div className="deleting-progress-banner">
            Deleting tracks… {deletingTotals.tracksDeleted.toLocaleString()}{' '}
            removed
            {deletingTotals.tracksUpdated
              ? ` · ${deletingTotals.tracksUpdated.toLocaleString()} updated`
              : ''}
          </div>
        )}

        {/* Bulk actions row (now under Music Folders header) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={selectedCount > 0}
              onChange={(e) =>
                window.dispatchEvent(
                  new CustomEvent(
                    e.target.checked
                      ? 'folder-manager/select-all'
                      : 'folder-manager/deselect-all',
                  ),
                )
              }
            />
            <span>{selectedCount > 0 ? 'Deselect All' : 'Select All'}</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IonButton
              fill="clear"
              size="small"
              color="danger"
              className="sources-btn icon-btn"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('folder-manager/remove-selected'),
                )
              }
              title="Remove selected folders"
              disabled={selectedCount === 0}
            >
              <IonIcon icon={closeOutline} />
              <span style={{ marginLeft: 6 }}>Remove</span>
            </IonButton>
            {/* Sync Selected temporarily removed per request */}
          </div>
        </div>

        <FolderManager
          mode="management"
          onComplete={handleFolderImportComplete}
          showAdvancedOptions={true}
          allowBulkOperations={false}
          autoRefreshLibrary={true}
          className="sources-folder-manager"
        />
      </div>

      {/* Reset Application Data Section */}
      <div className="reset-section">
        <ResetAppData />
      </div>
    </div>
  );
}
